import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { canAccessAdminPanel } from "../utils/adminAccess";
// import { useNavigate } from "react-router-dom";

// DeviceList Component
export default function DeviceList2() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestedDeviceId, setRequestedDeviceId] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestError, setRequestError] = useState("");
  const { user } = useContext(AuthContext);

  const isPrivilegedUser = canAccessAdminPanel(user);
  const canRequestDevice = Boolean(user) && !isPrivilegedUser;

  // Fetch data from API
  const fetchData = async () => {
    try {
      const response = await fetch("/api/devicelist", {
                    credentials: "include"
                });
      const data = await response.json();
      setDevices(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Run fetchData when component mounts
  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSubmit = async (event) => {
    event.preventDefault();

    const normalizedDeviceId = requestedDeviceId.trim().toUpperCase();
    if (!normalizedDeviceId) {
      setRequestError("Please enter device ID.");
      setRequestMessage("");
      return;
    }

    setIsSubmittingRequest(true);
    setRequestError("");
    setRequestMessage("");

    try {
      const response = await fetch("/api/device_request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          device_id: normalizedDeviceId,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        setRequestError(payload.error || "Failed to submit request.");
        return;
      }

      setRequestMessage(payload.message || "Request submitted successfully.");
      setRequestedDeviceId("");
      setShowRequestForm(false);
    } catch (error) {
      setRequestError("Failed to submit request.");
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  return (
    <div>
      {loading ? (
        <div className="spinner-container"><div className="spinner"></div></div>
      ) : (
        <table>
          <thead>
            <tr>
              <th className="dot-container"></th>
              <th>Device</th>
              <th>Voltage</th>
              <th>SOC</th>
              <th>Power</th>
              <th>Status</th>
              <th>ID</th>
              <th>Last update</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <DeviceRow key={device.device_id} device={device} />
            ))}
          </tbody>
          {canRequestDevice && (
            <tfoot>
              <tr>
                <td colSpan="8" style={{ backgroundColor: "#f2f4f8" }}>
                  {!showRequestForm ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowRequestForm(true);
                        setRequestError("");
                        setRequestMessage("");
                      }}
                      style={{
                        padding: "8px 14px",
                        borderRadius: "8px",
                        border: "1px solid #184a76",
                        backgroundColor: "#1f6fb2",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Request adding new device
                    </button>
                  ) : (
                    <form
                      onSubmit={handleRequestSubmit}
                      style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}
                    >
                      <label htmlFor="new-device-request-id">Device ID:</label>
                      <input
                        id="new-device-request-id"
                        type="text"
                        value={requestedDeviceId}
                        onChange={(event) => setRequestedDeviceId(event.target.value.toUpperCase())}
                        placeholder="Enter device ID"
                        required
                        style={{ padding: "7px 10px", borderRadius: "6px", border: "1px solid #bbb" }}
                      />
                      <button
                        type="submit"
                        disabled={isSubmittingRequest}
                        style={{
                          padding: "8px 14px",
                          borderRadius: "8px",
                          border: "1px solid #184a76",
                          backgroundColor: "#1f6fb2",
                          color: "#fff",
                          cursor: isSubmittingRequest ? "not-allowed" : "pointer",
                          opacity: isSubmittingRequest ? 0.7 : 1,
                        }}
                      >
                        {isSubmittingRequest ? "Sending..." : "Send request"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowRequestForm(false);
                          setRequestedDeviceId("");
                          setRequestError("");
                        }}
                        style={{
                          padding: "8px 14px",
                          borderRadius: "8px",
                          border: "1px solid #888",
                          backgroundColor: "#eee",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </form>
                  )}
                  {requestMessage && (
                    <div style={{ marginTop: "8px", color: "#166534" }}>{requestMessage}</div>
                  )}
                  {requestError && (
                    <div style={{ marginTop: "8px", color: "#b91c1c" }}>{requestError}</div>
                  )}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
}

// DeviceRow Component
function DeviceRow({ device }) {
  const {
    display_description,
    Voltage,
    SOC,
    Power,
    Status,
    device_id,
    updated,
    online,
    fw,
  } = device;

  let backgroundColor = "#fff";
  if (Status === "Warning") backgroundColor = "#ff9";
  if (Status === "Protect") backgroundColor = "#f99";
  if (Status === "Running") backgroundColor = "#9f9";
  if (!online || Status === "") {
    backgroundColor = "#aaa";
    device.Status = "Disconnected";
  }

  const encodedId = encodeURIComponent(device_id);
  const link = `/device/data?id=${encodedId}`;
  const updateLink = `/device/update?id=${encodedId}`;

  const parseFirmwareVersion = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleDeviceSelect = (event) => {
    const firmwareVersion = parseFirmwareVersion(fw);
    const shouldShowUpdatePrompt = false; //firmwareVersion !== null && firmwareVersion >= 3000 && firmwareVersion < 3029;

    if (!shouldShowUpdatePrompt) {
      return;
    }

    const accepted = window.confirm(
      `A new update is available for this device (firmware ${firmwareVersion}).\n\nPress OK to open the update page.`
    );

    if (accepted) {
      event.currentTarget.href = updateLink;
    }
  };

  return (
    <tr style={{ backgroundColor }}>
      <td>
        <div className="dot" style={{ backgroundColor }}></div>
      </td>
      <td>
        <a
          href={link}
          onClick={handleDeviceSelect}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {display_description || "N/A"}
        </a>
      </td>
      <td>{Voltage !== null ? Voltage * 1 : "N/A"} V</td>
      <td>{SOC !== null ? SOC * 1 : "N/A"} %</td>
      <td>{Power !== null ? (Power / 1000).toFixed(1) * 1 : "N/A"} kW</td>
      <td>{Status || "N/A"}</td>
      <td>{device_id}</td>
      <td>{updated || "N/A"}</td>
    </tr>
  );
}
